var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property, unsafeHTML } from '@inter-ikea-kompis/web-component';
import { NotificationDataSourceEnum } from '@inter-ikea-kompis/enums';
import { ChevronLeftSlashChevronRightIcon, LinkIcon, MailIcon, ScanQRCodeIcon } from '@inter-ikea-kompis/icons';
import { DexfSettingsUtility, UrlUtility } from '@inter-ikea-kompis/utilities';
import { ActionList, ActionListControlEnum, ActionListItem } from '@inter-ikea-kompis/component-action-list';
import { QrCode } from '@inter-ikea-kompis/component-qr-code';
import { SendByEmailStateEnum } from '@inter-ikea-kompis/component-send-by-email';
import { SendByEmail } from '@inter-ikea-kompis/component-send-by-email';
import { AspectRatioBox as SkapaAspectRatioBox } from '@ingka/aspect-ratio-box-webc/passive.js';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import { Text } from '@ingka/common-styles-webc';
import { checkmark, copy, link, registerIcon, warningTriangle } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import { Image as SkapaImage } from '@ingka/image-webc/passive.js';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';
import { DesignSourceEnum } from '@insights/insights-data-provider';
import ShareDesignCardCopyStateEnum from '../../enums/ShareDesignCardCopyStateEnum.js';
import ShareDesignCardStateEnum from '../../enums/ShareDesignCardStateEnum.js';
import ShareDesignCardTracker from '../../trackers/ShareDesignCardTracker.js';
import ShareDesignCardStyle from './ShareDesignCardStyle.js';
/**
 * Displays different options on how to share the solution.
 *
 * @event copyDesignCodeClick Triggered when the user clicks the copy design code button.
 * @event copyDesignCodeReset Triggered 3 seconds after copyDesignCodeState is set to success.
 * @event copyDesignLinkClick Triggered when the user clicks the copy design link button.
 * @event copyDesignLinkReset Triggered 3 seconds after copyDesignLinkState is set to success.
 * @event select Triggered when an item in the action list is clicked.
 * @event sendByEmailInput Triggered when the user writes in the sendByEmail view
 * @event sendByEmailReset Triggered 3 seconds after SendByEmailState is set to success.
 * @event sendByEmailSend Triggered when the user clicks send
 * @event sendByEmailValidation Triggered when the user tries to send with an invalid email
 */
export default class ShareDesignCard extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // The url to the solution saved. If not set, "Get link to design" is not shown. When copied, query parameter with vpc source is added. Required for rendering optional QR code option.
        this.designLink = null;
        // Set to true to show GetLink option in the default view. Requires the designLink to be set. Should be disabled in Kiosks.
        this.showGetLink = false;
        // Set to true to show QR code option in the default view. Requires the designLink to be set.
        this.showQrCode = false;
        // The current state of the component, e.g Share via email
        this.state = ShareDesignCardStateEnum.default;
        // Enable to allow autocompletion in the browser. Note that it should be disabled in Kiosks due to privacy policies, as it can otherwise store user data.
        this.autocomplete = false;
        // Disables outgoing links. Should be true in kiosks.
        this.disableLinks = false;
        // Set to true to show SendByEmail option in the default view
        this.showSendByEmail = false;
        // The current state of the SendByEmail view
        this.sendByEmailState = SendByEmailStateEnum.default;
        // The current value of the input in the SendByEmail view
        this.sendByEmailValue = '';
        // If the current value of the sendByEmail input field is valid or not
        this.sendByEmailInvalidInput = false;
        // Shows the button in the copyDesignCode view. Should be false in kiosks.
        this.showCopyDesignCodeButton = false;
        // The current state for the copy design code button
        this.copyDesignCodeState = ShareDesignCardCopyStateEnum.default;
        // The current state for the copy design link button
        this.copyDesignLinkState = ShareDesignCardCopyStateEnum.default;
        // The current design screenshot image.
        this.screenshotUrl = null;
        // The current design screenshot image alt text.
        this.screenshotAlt = null;
        // The current design screenshot image product name.
        this.productName = '';
        // The current design screenshot image product description.
        this.productDescription = '';
        // Used for automatic analytics. If null or not set, analytics is turned off.
        this.analytics = null;
        this.copyDesignCodeTimeout = null;
        this.copyDesignLinkTimeout = null;
        this.tracker = null;
        this.dexfSettings = null;
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.hasTranslationsAndSettings()) {
            // We can't throw an error because on the first render no props are set.
            return null;
        }
        return html `
			<style>
				${ShareDesignCardStyle.getStyle({ theme: this.theme })}
			</style>
			${this.getViewForState()}
		`;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.copyDesignCodeTimeout) {
            clearTimeout(this.copyDesignCodeTimeout);
        }
        if (this.copyDesignLinkTimeout) {
            clearTimeout(this.copyDesignLinkTimeout);
        }
        this.disconnectTracker();
    }
    async connectedCallback() {
        registerIcon(checkmark);
        registerIcon(copy);
        registerIcon(link);
        registerIcon(warningTriangle);
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('skapa-image', SkapaImage);
        this.customElements.define('skapa-inline-message', SkapaInlineMessage);
        this.customElements.define('skapa-aspect-ratio', SkapaAspectRatioBox);
        this.customElements.define('kompis-action-list', ActionList);
        this.customElements.define('kompis-action-list-item', ActionListItem);
        this.customElements.define('kompis-qr-code', QrCode);
        this.customElements.define('kompis-send-by-email', SendByEmail);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('copyDesignCodeState')) {
            if (this.copyDesignCodeState === ShareDesignCardCopyStateEnum.success) {
                if (this.copyDesignCodeTimeout) {
                    clearTimeout(this.copyDesignCodeTimeout);
                }
                this.copyDesignCodeTimeout = setTimeout(() => this.dispatchEvent(new CustomEvent('copyDesignCodeReset', { bubbles: true })), 3000);
            }
        }
        if (changedProperties.has('copyDesignLinkState')) {
            if (this.copyDesignLinkState === ShareDesignCardCopyStateEnum.success) {
                if (this.copyDesignLinkTimeout) {
                    clearTimeout(this.copyDesignLinkTimeout);
                }
                this.copyDesignLinkTimeout = setTimeout(() => this.dispatchEvent(new CustomEvent('copyDesignLinkReset', { bubbles: true })), 3000);
            }
        }
        if (changedProperties.has('localizedInformation') &&
            this.localizedInformation &&
            !this.settings) {
            this.dexfSettings = DexfSettingsUtility.parseLocalizedInformation(this.localizedInformation);
        }
        if (changedProperties.has('settings') && this.settings) {
            this.dexfSettings = this.settings;
        }
        if (changedProperties.has('analytics')) {
            this.disconnectTracker();
            this.connectTracker().then(() => {
                this.tracker?.sendInitialConfiguration({
                    showEmailOption: this.showSendByEmailOption(),
                    showCopyLinkOption: this.showGetLinkOption(),
                    showCopyCodeButton: this.showCopyDesignCodeButton,
                    showQrCodeOption: this.showQrCodeOption()
                });
            });
        }
        return true;
    }
    getSettings() {
        return this.dexfSettings;
    }
    hasTranslationsAndSettings() {
        return !!this.localizedInformation || !!(this.translations && this.settings);
    }
    getTranslations() {
        const translations = this.translations
            ? this.translations
            : this.localizedInformation?.translations;
        return translations;
    }
    async connectTracker() {
        if (this.analytics) {
            const reporter = this.analytics.__INTERNAL__reporter;
            if (reporter?.connected === false) {
                // Should use an existing reporter, but wait for it to be connected
                return;
            }
            // Use existing connected reporter, or the tracker should create a new one.
            const reporterId = reporter ? reporter.id : undefined;
            this.tracker = new ShareDesignCardTracker({
                ...this.analytics,
                settings: this.getSettings(),
                locale: this.locale,
                reporterId
            });
            await this.tracker?.connect();
        }
    }
    disconnectTracker() {
        this.tracker?.disconnect();
        this.tracker = null;
    }
    /**
     * Returns the view for the component depending on state.
     */
    getViewForState() {
        switch (this.state) {
            case ShareDesignCardStateEnum.sendByEmail:
                return this.getSendByEmail();
            case ShareDesignCardStateEnum.getDesignCode:
                return this.getDesignCode();
            case ShareDesignCardStateEnum.getLink:
                return this.getLinkToDesign();
            case ShareDesignCardStateEnum.qrCode:
                return this.getQrCode();
            default:
                return this.getDefault();
        }
    }
    /**
     * @returns True if notifications are enabled in localizedInformation.
     */
    notificationsAreEnabled() {
        const { notificationDataSource } = this.getSettings().kompis.serviceSettings;
        return notificationDataSource !== NotificationDataSourceEnum.disabled;
    }
    /**
     * @returns True if showSendByEmail is true and notifications are enabled in localizedInformation.
     */
    showSendByEmailOption() {
        return this.showSendByEmail && this.notificationsAreEnabled();
    }
    /**
     * @returns True if designLink is provided, showGetLink is true and disableLinks is false.
     */
    showGetLinkOption() {
        return !!this.designLink && this.showGetLink && !this.disableLinks;
    }
    showQrCodeOption() {
        return !!this.designLink && this.showQrCode;
    }
    onlyDesignCodeViewIsActivated() {
        return !this.showSendByEmailOption() && !this.showGetLinkOption() && !this.showQrCodeOption();
    }
    /**
     * Get default view.
     */
    getDefault() {
        if (this.onlyDesignCodeViewIsActivated()) {
            return html `${this.getTitle(ShareDesignCardStateEnum.default)} ${this.getProductDescription()}
			${this.getDesignCodeBody()}`;
        }
        return html `<div class="container">
			${this.getTitle(ShareDesignCardStateEnum.default)} ${this.getProductDescription()}
			${this.getActionList()}
		</div>`;
    }
    /**
     * Get title for view.
     *
     * @param view ...
     */
    getTitle(view) {
        const { shareDesignCardDefaultTitle, shareDesignCardSendByEmailTitle, shareDesignCardQrCodeTitle, shareDesignCardGetDesignCodeTitle, shareDesignCardGetLinkTitle } = this.getTranslations();
        const titles = {
            [ShareDesignCardStateEnum.default]: shareDesignCardDefaultTitle,
            [ShareDesignCardStateEnum.sendByEmail]: shareDesignCardSendByEmailTitle,
            [ShareDesignCardStateEnum.qrCode]: shareDesignCardQrCodeTitle,
            [ShareDesignCardStateEnum.getDesignCode]: shareDesignCardGetDesignCodeTitle,
            [ShareDesignCardStateEnum.getLink]: shareDesignCardGetLinkTitle
        };
        return html `<h2 class="title ${Text.classnames({ heading: { m: true } })}">
			${titles[view]}
		</h2>`;
    }
    /**
     * Get Action List.
     */
    getActionList() {
        const sendByEmail = this.showSendByEmailOption()
            ? this.getActionListItem(ShareDesignCardStateEnum.sendByEmail)
            : '';
        const qrCode = this.showQrCodeOption()
            ? this.getActionListItem(ShareDesignCardStateEnum.qrCode)
            : '';
        const getDesignCode = this.getActionListItem(ShareDesignCardStateEnum.getDesignCode);
        const getDesignLink = this.showGetLinkOption()
            ? this.getActionListItem(ShareDesignCardStateEnum.getLink)
            : '';
        return html `<kompis-action-list>
			${sendByEmail} ${qrCode} ${getDesignCode} ${getDesignLink}
		</kompis-action-list>`;
    }
    /**
     * Get Action List Item.
     *
     * @param state ...
     */
    getActionListItem(state) {
        const { shareDesignCardSendByEmailLabel, shareDesignCardQrCodeLabel, shareDesignCardGetDesignCodeLabel, shareDesignCardGetLinkLabel } = this.getTranslations();
        const labels = {
            [ShareDesignCardStateEnum.sendByEmail]: shareDesignCardSendByEmailLabel,
            [ShareDesignCardStateEnum.qrCode]: shareDesignCardQrCodeLabel,
            [ShareDesignCardStateEnum.getDesignCode]: shareDesignCardGetDesignCodeLabel,
            [ShareDesignCardStateEnum.getLink]: shareDesignCardGetLinkLabel
        };
        const icons = {
            [ShareDesignCardStateEnum.sendByEmail]: MailIcon,
            [ShareDesignCardStateEnum.qrCode]: ScanQRCodeIcon,
            [ShareDesignCardStateEnum.getDesignCode]: ChevronLeftSlashChevronRightIcon,
            [ShareDesignCardStateEnum.getLink]: LinkIcon
        };
        return html `
			<kompis-action-list-item
				.theme="${this.theme}"
				.label="${labels[state]}"
				.leadingIcon="${icons[state]}"
				.control="${ActionListControlEnum.chevron}"
				@select="${() => {
            this.dispatchEvent(new CustomEvent('select', {
                bubbles: true,
                detail: { selectedState: state }
            }));
            this.tracker?.sendSelectOption(state);
        }}"
			></kompis-action-list-item>
		`;
    }
    /**
     * Get description for view.
     *
     * @param view ...
     */
    getDescription(view) {
        const { shareDesignCardSendByEmailDescription, shareDesignCardGetDesignCodeDescription, shareDesignCardGetLinkDescription } = this.getTranslations();
        const descriptions = {
            [ShareDesignCardStateEnum.sendByEmail]: shareDesignCardSendByEmailDescription,
            [ShareDesignCardStateEnum.getDesignCode]: shareDesignCardGetDesignCodeDescription,
            [ShareDesignCardStateEnum.getLink]: shareDesignCardGetLinkDescription
        };
        return html `<p class="description ${Text.classnames({ body: { m: true } })}">
			${unsafeHTML(descriptions[view].replace(/[\n\r]+/gm, '<div class="spacer" aria-hidden="true"></div>'))}
		</p>`;
    }
    /**
     * Get default description.
     */
    getProductDescription() {
        return html ` ${this.getScreenshot()}
			<div class="default-description">
				<h3 class="${Text.classnames({ heading: { xs: true } })}">
					${this.productName.toUpperCase()}
				</h3>
				<span class="${Text.classnames({ body: { m: true } })}">${this.productDescription}</span>
			</div>`;
    }
    /**
     * Get screenshot if available.
     */
    getScreenshot() {
        if (!this.screenshotUrl) {
            return null;
        }
        return html ` <skapa-aspect-ratio ratio="wide">
			<skapa-image ?fallback="${null}">
				<img
					src="${this.screenshotUrl}"
					alt="${this.screenshotAlt ?? this.getTranslations().summaryPageScreenshotAltText}"
				/>
			</skapa-image>
		</skapa-aspect-ratio>`;
    }
    /**
     * Get Send By Email.
     */
    getSendByEmail() {
        return html `${this.getTitle(ShareDesignCardStateEnum.sendByEmail)}
			${this.getDescription(ShareDesignCardStateEnum.sendByEmail)}
			<kompis-send-by-email
				.theme="${this.theme}"
				.localizedInformation="${this.localizedInformation}"
				.translations="${this.translations}"
				.settings="${this.settings}"
				.state="${this.sendByEmailState}"
				.value="${this.sendByEmailValue}"
				.invalidInput="${this.sendByEmailInvalidInput}"
				.autocomplete="${this.autocomplete}"
				.disableLinks="${this.disableLinks}"
				@input="${({ detail }) => this.dispatchEvent(new CustomEvent('sendByEmailInput', { bubbles: true, detail }))}"
				@validation="${({ detail }) => {
            const { inputElement, ...restOfDetail } = detail;
            this.dispatchEvent(new CustomEvent('sendByEmailValidation', { bubbles: true, detail: restOfDetail }));
            inputElement?.focus();
        }}"
				@send="${({ detail }) => {
            this.dispatchEvent(new CustomEvent('sendByEmailSend', { bubbles: true, detail }));
            this.tracker?.sendSendByEmail(this.vpcCode);
        }}"
				@reset="${({ detail }) => this.dispatchEvent(new CustomEvent('sendByEmailReset', { bubbles: true, detail }))}"
			>
			</kompis-send-by-email>`;
    }
    /**
     * Click event for button for copy.
     */
    async onCopyClick() {
        let thrownError = null;
        const linkToCopy = `${UrlUtility.addQueryParameter(this.designLink, 'vpcSource', DesignSourceEnum.clipboard)}`;
        const onVpcCodeView = this.state === ShareDesignCardStateEnum.getDesignCode || this.onlyDesignCodeViewIsActivated();
        const stringToCopy = onVpcCodeView ? this.vpcCode : linkToCopy;
        const eventName = onVpcCodeView ? 'copyDesignCodeClick' : 'copyDesignLinkClick';
        try {
            await navigator.clipboard.writeText(stringToCopy);
        }
        catch (error) {
            thrownError = error;
        }
        this.dispatchEvent(new CustomEvent(eventName, {
            bubbles: true,
            detail: {
                state: !thrownError
                    ? ShareDesignCardCopyStateEnum.success
                    : ShareDesignCardCopyStateEnum.failed,
                error: thrownError?.message
            }
        }));
        if (onVpcCodeView) {
            this.tracker?.sendCopyCode(this.vpcCode);
        }
        else {
            this.tracker?.sendCopyLink(this.vpcCode);
        }
        if (thrownError) {
            throw thrownError;
        }
    }
    /**
     * Get body of design code view.
     */
    getDesignCodeBody() {
        const primaryVariant = 'primary';
        const { shareDesignCardGetDesignCodeButtonSuccess, shareDesignCardGetDesignCodeButtonDefault, shareDesignCardGetDesignCodeDesignCode } = this.getTranslations();
        let buttonLabel;
        let buttonIcon;
        switch (this.copyDesignCodeState) {
            case ShareDesignCardCopyStateEnum.success:
                buttonLabel = shareDesignCardGetDesignCodeButtonSuccess;
                buttonIcon = 'checkmark';
                break;
            default:
                buttonLabel = shareDesignCardGetDesignCodeButtonDefault;
                buttonIcon = 'copy';
                break;
        }
        return html `
			${this.getDescription(ShareDesignCardStateEnum.getDesignCode)}
			<div class="designCodeText">
				<p class="description ${Text.classnames({ body: { l: true } })}">
					${shareDesignCardGetDesignCodeDesignCode + ': '} <strong>${this.vpcCode}</strong>
				</p>
			</div>
			${this.showCopyDesignCodeButton
            ? html `<skapa-button
							variant="${primaryVariant}"
							fluid
							@click="${this.onCopyClick.bind(this)}"
							role="${this.copyDesignCodeState === ShareDesignCardCopyStateEnum.success
                ? 'status'
                : 'none'}"
						>
							<skapa-icon icon="${buttonIcon}" slot="icon"></skapa-icon>
							${buttonLabel}
						</skapa-button>
						${this.getCopyCodeFailedError()}`
            : null}
		`;
    }
    /**
     * Get Design Code view.
     */
    getDesignCode() {
        return html `${this.getTitle(ShareDesignCardStateEnum.getDesignCode)} ${this.getDesignCodeBody()}`;
    }
    getCopyFailedError() {
        const cautionaryVariant = 'cautionary';
        const { ikeaGenericErrorTryAgain } = this.getTranslations();
        return html `<div role="status" aria-describedby="${ikeaGenericErrorTryAgain}">
			<skapa-inline-message
				subtle
				variant="${cautionaryVariant}"
				id="errorDescription"
				role="alert"
			>
				<skapa-icon slot="message-icon" icon="warning-triangle" aria-hidden="true"></skapa-icon>
				<span slot="body">${ikeaGenericErrorTryAgain}</span>
			</skapa-inline-message>
		</div>`;
    }
    getCopyCodeFailedError() {
        if (this.copyDesignCodeState !== ShareDesignCardCopyStateEnum.failed) {
            return null;
        }
        return this.getCopyFailedError();
    }
    getCopyLinkFailedError() {
        if (this.copyDesignLinkState !== ShareDesignCardCopyStateEnum.failed) {
            return null;
        }
        return this.getCopyFailedError();
    }
    /**
     * Get Link To Design view.
     */
    getLinkToDesign() {
        const primaryVariant = 'primary';
        const { shareDesignCardGetLinkButtonSuccess, shareDesignCardGetLinkButtonDefault } = this.getTranslations();
        let buttonLabel;
        let buttonIcon;
        let buttonRole;
        switch (this.copyDesignLinkState) {
            case ShareDesignCardCopyStateEnum.success:
                buttonLabel = shareDesignCardGetLinkButtonSuccess;
                buttonIcon = 'checkmark';
                buttonRole = 'status';
                break;
            default:
                buttonLabel = shareDesignCardGetLinkButtonDefault;
                buttonIcon = 'link';
                buttonRole = 'none';
                break;
        }
        return html `${this.getTitle(ShareDesignCardStateEnum.getLink)}
			${this.getDescription(ShareDesignCardStateEnum.getLink)}
			<skapa-button
				variant="${primaryVariant}"
				role="${buttonRole}"
				fluid
				@click="${this.onCopyClick.bind(this)}"
			>
				<skapa-icon icon="${buttonIcon}" slot="icon"></skapa-icon>
				${buttonLabel}
			</skapa-button>
			${this.getCopyLinkFailedError()}`;
    }
    getQrCode() {
        if (!this.showQrCodeOption()) {
            return null;
        }
        const { shareDesignCardQrCodeDescription, shareDesignCardDesignChangeDisclaimer } = this.getTranslations();
        return html `${this.getTitle(ShareDesignCardStateEnum.qrCode)}
			<div class="qr-code">
				<div class="qr-code-container">
					<kompis-qr-code
						.url="${UrlUtility.addQueryParameter(this.designLink, 'vpcSource', DesignSourceEnum.qr)}"
						.qrCodeTitle="${shareDesignCardQrCodeDescription}"
					></kompis-qr-code>
				</div>
				<p class="qr-code-text ${Text.classnames({ heading: { s: true } })}">
					${shareDesignCardQrCodeDescription}
				</p>
			</div>
			<p class="design-changes-disclaimer ${Text.classnames({ body: { m: true } })}">
				${shareDesignCardDesignChangeDisclaimer}
			</p>`;
    }
}
__decorate([
    property({ type: Object, required: true })
], ShareDesignCard.prototype, "theme", void 0);
__decorate([
    property({ type: Object })
], ShareDesignCard.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], ShareDesignCard.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], ShareDesignCard.prototype, "settings", void 0);
__decorate([
    property({ type: String, required: true })
], ShareDesignCard.prototype, "locale", void 0);
__decorate([
    property({ type: String, required: true })
], ShareDesignCard.prototype, "vpcCode", void 0);
__decorate([
    property({ type: String })
], ShareDesignCard.prototype, "designLink", void 0);
__decorate([
    property({ type: Boolean })
], ShareDesignCard.prototype, "showGetLink", void 0);
__decorate([
    property({ type: Boolean })
], ShareDesignCard.prototype, "showQrCode", void 0);
__decorate([
    property({ type: String })
], ShareDesignCard.prototype, "state", void 0);
__decorate([
    property({ type: Boolean })
], ShareDesignCard.prototype, "autocomplete", void 0);
__decorate([
    property({ type: Boolean })
], ShareDesignCard.prototype, "disableLinks", void 0);
__decorate([
    property({ type: Boolean })
], ShareDesignCard.prototype, "showSendByEmail", void 0);
__decorate([
    property({ type: String })
], ShareDesignCard.prototype, "sendByEmailState", void 0);
__decorate([
    property({ type: String })
], ShareDesignCard.prototype, "sendByEmailValue", void 0);
__decorate([
    property({ type: Boolean })
], ShareDesignCard.prototype, "sendByEmailInvalidInput", void 0);
__decorate([
    property({ type: Boolean })
], ShareDesignCard.prototype, "showCopyDesignCodeButton", void 0);
__decorate([
    property({ type: String })
], ShareDesignCard.prototype, "copyDesignCodeState", void 0);
__decorate([
    property({ type: String })
], ShareDesignCard.prototype, "copyDesignLinkState", void 0);
__decorate([
    property({ type: String })
], ShareDesignCard.prototype, "screenshotUrl", void 0);
__decorate([
    property({ type: String })
], ShareDesignCard.prototype, "screenshotAlt", void 0);
__decorate([
    property({ type: String })
], ShareDesignCard.prototype, "productName", void 0);
__decorate([
    property({ type: String })
], ShareDesignCard.prototype, "productDescription", void 0);
__decorate([
    property({ type: Object })
], ShareDesignCard.prototype, "analytics", void 0);
//# sourceMappingURL=ShareDesignCard.js.map